import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PassStatus } from '@sss/types';
import CopyButton from '../components/Secret/CopyButton';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { downloadPass } from '../lib/passFile';
import { formatBytes } from '../lib/tools';
import { forgetPendingPass, forgetPendingTopUp, getCheckoutStatus, getPassStatus, loadPendingPass, loadPendingTopUp, relayProblem, savePass } from '../lib/relayApi';
import ui from '../styles/ui.module.css';
import styles from './RevealPage.module.css';

/** Stripe's webhook usually lands within seconds; give it two minutes before saying so. */
const POLL_MS = 2000;
const POLL_TRIES = 60;

type State = { step: 'none' } | { step: 'waiting' } | { step: 'slow' } | { step: 'failed' } | { step: 'ready'; pass: string; status: PassStatus };

/**
 * /pass/bought — where Stripe sends the buyer back. Waits for the payment
 * to arrive (the webhook turns the pending token into a pass), then keeps
 * the pass in this browser. The file form in the other tab hears about it
 * through the storage event and picks it up.
 */
export default function PassBoughtPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('pageTitle.passBought'));
  const [pending] = useState(loadPendingPass);
  // A top-up's pass already works, so wait on its checkout instead (docs/adr/0015).
  const [topUp] = useState(loadPendingTopUp);
  const [state, setState] = useState<State>(pending ? { step: 'waiting' } : { step: 'none' });

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (tries: number) => {
      try {
        if (topUp) {
          const { status: checkout } = await getCheckoutStatus(topUp);
          if (cancelled) return;
          if (checkout === 'failed' || checkout === 'expired') {
            forgetPendingPass();
            forgetPendingTopUp();
            setState({ step: 'failed' });
            return;
          }
          if (checkout !== 'paid') throw new Error('not paid yet');
        }
        const status = await getPassStatus(pending);
        if (cancelled) return;
        savePass(pending);
        forgetPendingPass();
        forgetPendingTopUp();
        setState({ step: 'ready', pass: pending, status });
        return;
      } catch (err) {
        if (cancelled) return;
        const problem = relayProblem(err);
        // Not found means the checkout expired or failed; pending (or a blip) means keep waiting.
        if (problem === 'passNotFound' || problem === 'passExpired') {
          forgetPendingPass();
          setState({ step: 'failed' });
          return;
        }
        if (tries + 1 === POLL_TRIES) setState({ step: 'slow' });
      }
      timer = setTimeout(() => void poll(tries + 1), POLL_MS);
    };
    void poll(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pending, topUp]);

  return (
    <main className={styles.page}>
      <div className={styles.stack}>
        <p className={ui.eyebrow}>{t('pass.eyebrow')}</p>
        {state.step === 'none' && (
          <>
            <h1 className={styles.title}>{t('pass.bought.none.title')}</h1>
            <p className={styles.lead}>{t('pass.bought.none.body')}</p>
          </>
        )}
        {(state.step === 'waiting' || state.step === 'slow') && (
          <>
            <h1 className={styles.title}>{t('pass.bought.waiting.title')}</h1>
            <p className={styles.lead} role="status">
              {t(state.step === 'slow' ? 'pass.bought.waiting.slow' : 'pass.bought.waiting.body')}
            </p>
          </>
        )}
        {state.step === 'failed' && (
          <>
            <h1 className={styles.title}>{t('pass.bought.failed.title')}</h1>
            <p className={styles.lead}>{t('pass.bought.failed.body')}</p>
          </>
        )}
        {state.step === 'ready' && (
          <>
            <h1 className={styles.title}>{t('pass.bought.ready.title')}</h1>
            <p className={styles.lead} role="status">
              {t('pass.bought.ready.body', {
                size: formatBytes(state.status.bytesTotal),
                date: new Date(state.status.expiresAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }),
              })}
            </p>
            <div className={secretStyles.actions}>
              <Link to="/live?kind=files" className={ui.button}>
                {t('pass.bought.ready.send')}
              </Link>
            </div>
            <section className={styles.stack} aria-labelledby="keep-pass">
              <h2 id="keep-pass" className={ui.eyebrow}>
                {t('pass.bought.ready.keepTitle')}
              </h2>
              <p className={ui.hint}>{t('pass.bought.ready.keep')}</p>
              <div className={secretStyles.actions}>
                <button type="button" className={ui.buttonSecondary} onClick={() => downloadPass(state.pass, state.status, i18n.language)}>
                  {t('pass.bought.ready.download')}
                </button>
                <CopyButton value={state.pass} label={t('pass.bought.ready.copy')} />
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
